// ═══════════════════════════════════════════════════════════════
// Catálogo BNCC Enriquecido — dados estruturados por componente
// Fonte: MEC/BNCC 2018 | Atualizado: 2026-04-24
// ═══════════════════════════════════════════════════════════════

export interface CatalogoItem {
  nome: string;
  codigo: string;
  siglaBNCC: string | null;
  areaDoCOnhecimento: string;
  etapasAplicaveis: string[];
  obrigatoria: boolean;
  corVisual: string;
  aulasSemanaisSugeridas: Record<string, number>;
}

export interface CatalogoGrupo {
  grupo: string;
  etapa: string; // para filtro de etapa
  itens: CatalogoItem[];
}

export const ETAPAS = ['Ed. Infantil', 'Fund. I', 'Fund. II', 'Médio'] as const;
export type Etapa = (typeof ETAPAS)[number];

export const AREAS_CONHECIMENTO = [
  'Linguagens',
  'Matemática',
  'Ciências da Natureza',
  'Ciências Humanas',
  'Ensino Religioso',
  'Linguagens e suas Tecnologias',
  'Matemática e suas Tecnologias',
  'Ciências da Natureza e suas Tecnologias',
  'Ciências Humanas e Sociais Aplicadas',
  'Complementar / Parte Diversificada',
];

// ── Catálogo Flat (mais fácil de filtrar) ──────────────────────────

export const CATALOGO_BNCC: CatalogoItem[] = [
  // ── LINGUAGENS — Fund. I + II + Médio ──────────────────────────
  {
    nome: 'Língua Portuguesa',
    codigo: 'LP',
    siglaBNCC: 'LP',
    areaDoCOnhecimento: 'Linguagens',
    etapasAplicaveis: ['Ed. Infantil', 'Fund. I', 'Fund. II', 'Médio'],
    obrigatoria: true,
    corVisual: '#3b82f6',
    aulasSemanaisSugeridas: {
      'Maternal': 0, '1º Ano': 6, '2º Ano': 6, '3º Ano': 5,
      '4º Ano': 5, '5º Ano': 5, '6º Ano': 4, '7º Ano': 4,
      '8º Ano': 4, '9º Ano': 4, '1ª Série': 3, '2ª Série': 3, '3ª Série': 3,
    },
  },
  {
    nome: 'Arte',
    codigo: 'ART',
    siglaBNCC: 'AR',
    areaDoCOnhecimento: 'Linguagens',
    etapasAplicaveis: ['Ed. Infantil', 'Fund. I', 'Fund. II', 'Médio'],
    obrigatoria: true,
    corVisual: '#ec4899',
    aulasSemanaisSugeridas: {
      '1º Ano': 2, '2º Ano': 2, '3º Ano': 2, '4º Ano': 2, '5º Ano': 2,
      '6º Ano': 2, '7º Ano': 2, '8º Ano': 2, '9º Ano': 2,
      '1ª Série': 1, '2ª Série': 1, '3ª Série': 1,
    },
  },
  {
    nome: 'Educação Física',
    codigo: 'EDF',
    siglaBNCC: 'EF',
    areaDoCOnhecimento: 'Linguagens',
    etapasAplicaveis: ['Ed. Infantil', 'Fund. I', 'Fund. II', 'Médio'],
    obrigatoria: true,
    corVisual: '#f97316',
    aulasSemanaisSugeridas: {
      '1º Ano': 2, '2º Ano': 2, '3º Ano': 2, '4º Ano': 2, '5º Ano': 2,
      '6º Ano': 2, '7º Ano': 2, '8º Ano': 2, '9º Ano': 2,
      '1ª Série': 2, '2ª Série': 2, '3ª Série': 2,
    },
  },
  {
    nome: 'Língua Inglesa',
    codigo: 'ING',
    siglaBNCC: 'LI',
    areaDoCOnhecimento: 'Linguagens',
    etapasAplicaveis: ['Fund. II', 'Médio'],
    obrigatoria: true,
    corVisual: '#06b6d4',
    aulasSemanaisSugeridas: {
      '6º Ano': 2, '7º Ano': 2, '8º Ano': 2, '9º Ano': 2,
      '1ª Série': 2, '2ª Série': 2, '3ª Série': 2,
    },
  },
  // ── MATEMÁTICA ──────────────────────────────────────────────────
  {
    nome: 'Matemática',
    codigo: 'MAT',
    siglaBNCC: 'MA',
    areaDoCOnhecimento: 'Matemática',
    etapasAplicaveis: ['Ed. Infantil', 'Fund. I', 'Fund. II', 'Médio'],
    obrigatoria: true,
    corVisual: '#8b5cf6',
    aulasSemanaisSugeridas: {
      '1º Ano': 5, '2º Ano': 5, '3º Ano': 5, '4º Ano': 5, '5º Ano': 5,
      '6º Ano': 5, '7º Ano': 5, '8º Ano': 5, '9º Ano': 5,
      '1ª Série': 3, '2ª Série': 3, '3ª Série': 3,
    },
  },
  // ── CIÊNCIAS DA NATUREZA ────────────────────────────────────────
  {
    nome: 'Ciências',
    codigo: 'CIE',
    siglaBNCC: 'CI',
    areaDoCOnhecimento: 'Ciências da Natureza',
    etapasAplicaveis: ['Fund. I', 'Fund. II'],
    obrigatoria: true,
    corVisual: '#10b981',
    aulasSemanaisSugeridas: {
      '1º Ano': 2, '2º Ano': 2, '3º Ano': 2, '4º Ano': 2, '5º Ano': 2,
      '6º Ano': 3, '7º Ano': 3, '8º Ano': 3, '9º Ano': 3,
    },
  },
  // ── CIÊNCIAS HUMANAS ────────────────────────────────────────────
  {
    nome: 'História',
    codigo: 'HIS',
    siglaBNCC: 'HI',
    areaDoCOnhecimento: 'Ciências Humanas',
    etapasAplicaveis: ['Fund. I', 'Fund. II', 'Médio'],
    obrigatoria: true,
    corVisual: '#f59e0b',
    aulasSemanaisSugeridas: {
      '1º Ano': 2, '2º Ano': 2, '3º Ano': 2, '4º Ano': 2, '5º Ano': 2,
      '6º Ano': 2, '7º Ano': 2, '8º Ano': 2, '9º Ano': 2,
      '1ª Série': 2, '2ª Série': 2, '3ª Série': 2,
    },
  },
  {
    nome: 'Geografia',
    codigo: 'GEO',
    siglaBNCC: 'GE',
    areaDoCOnhecimento: 'Ciências Humanas',
    etapasAplicaveis: ['Fund. I', 'Fund. II', 'Médio'],
    obrigatoria: true,
    corVisual: '#84cc16',
    aulasSemanaisSugeridas: {
      '1º Ano': 2, '2º Ano': 2, '3º Ano': 2, '4º Ano': 2, '5º Ano': 2,
      '6º Ano': 2, '7º Ano': 2, '8º Ano': 2, '9º Ano': 2,
      '1ª Série': 2, '2ª Série': 2, '3ª Série': 2,
    },
  },
  // ── ENSINO RELIGIOSO ────────────────────────────────────────────
  {
    nome: 'Ensino Religioso',
    codigo: 'ER',
    siglaBNCC: 'ER',
    areaDoCOnhecimento: 'Ensino Religioso',
    etapasAplicaveis: ['Fund. I', 'Fund. II'],
    obrigatoria: true,
    corVisual: '#a78bfa',
    aulasSemanaisSugeridas: {
      '1º Ano': 1, '2º Ano': 1, '3º Ano': 1, '4º Ano': 1, '5º Ano': 1,
      '6º Ano': 1, '7º Ano': 1, '8º Ano': 1, '9º Ano': 1,
    },
  },
  // ── ENSINO MÉDIO ────────────────────────────────────────────────
  {
    nome: 'Biologia',
    codigo: 'BIO',
    siglaBNCC: 'BI',
    areaDoCOnhecimento: 'Ciências da Natureza e suas Tecnologias',
    etapasAplicaveis: ['Médio'],
    obrigatoria: true,
    corVisual: '#059669',
    aulasSemanaisSugeridas: { '1ª Série': 2, '2ª Série': 2, '3ª Série': 2 },
  },
  {
    nome: 'Física',
    codigo: 'FIS',
    siglaBNCC: 'FI',
    areaDoCOnhecimento: 'Ciências da Natureza e suas Tecnologias',
    etapasAplicaveis: ['Médio'],
    obrigatoria: true,
    corVisual: '#0ea5e9',
    aulasSemanaisSugeridas: { '1ª Série': 2, '2ª Série': 2, '3ª Série': 2 },
  },
  {
    nome: 'Química',
    codigo: 'QUI',
    siglaBNCC: 'QI',
    areaDoCOnhecimento: 'Ciências da Natureza e suas Tecnologias',
    etapasAplicaveis: ['Médio'],
    obrigatoria: true,
    corVisual: '#7c3aed',
    aulasSemanaisSugeridas: { '1ª Série': 2, '2ª Série': 2, '3ª Série': 2 },
  },
  {
    nome: 'Sociologia',
    codigo: 'SOC',
    siglaBNCC: 'SO',
    areaDoCOnhecimento: 'Ciências Humanas e Sociais Aplicadas',
    etapasAplicaveis: ['Médio'],
    obrigatoria: true,
    corVisual: '#dc2626',
    aulasSemanaisSugeridas: { '1ª Série': 1, '2ª Série': 1, '3ª Série': 1 },
  },
  {
    nome: 'Filosofia',
    codigo: 'FIL',
    siglaBNCC: 'FL',
    areaDoCOnhecimento: 'Ciências Humanas e Sociais Aplicadas',
    etapasAplicaveis: ['Médio'],
    obrigatoria: true,
    corVisual: '#0891b2',
    aulasSemanaisSugeridas: { '1ª Série': 1, '2ª Série': 1, '3ª Série': 1 },
  },
  // ── COMPLEMENTARES / PARTE DIVERSIFICADA ────────────────────────
  {
    nome: 'Espanhol',
    codigo: 'ESP',
    siglaBNCC: null,
    areaDoCOnhecimento: 'Complementar / Parte Diversificada',
    etapasAplicaveis: ['Fund. II', 'Médio'],
    obrigatoria: false,
    corVisual: '#d97706',
    aulasSemanaisSugeridas: { '6º Ano': 2, '7º Ano': 2, '8º Ano': 2, '9º Ano': 2, '1ª Série': 1, '2ª Série': 1, '3ª Série': 1 },
  },
  {
    nome: 'Informática',
    codigo: 'INF',
    siglaBNCC: null,
    areaDoCOnhecimento: 'Complementar / Parte Diversificada',
    etapasAplicaveis: ['Fund. I', 'Fund. II', 'Médio'],
    obrigatoria: false,
    corVisual: '#6366f1',
    aulasSemanaisSugeridas: {},
  },
  {
    nome: 'Robótica',
    codigo: 'ROB',
    siglaBNCC: null,
    areaDoCOnhecimento: 'Complementar / Parte Diversificada',
    etapasAplicaveis: ['Fund. II', 'Médio'],
    obrigatoria: false,
    corVisual: '#374151',
    aulasSemanaisSugeridas: {},
  },
  {
    nome: 'Música',
    codigo: 'MUS',
    siglaBNCC: null,
    areaDoCOnhecimento: 'Complementar / Parte Diversificada',
    etapasAplicaveis: ['Ed. Infantil', 'Fund. I', 'Fund. II'],
    obrigatoria: false,
    corVisual: '#be185d',
    aulasSemanaisSugeridas: {},
  },
  {
    nome: 'Teatro',
    codigo: 'TEA',
    siglaBNCC: null,
    areaDoCOnhecimento: 'Complementar / Parte Diversificada',
    etapasAplicaveis: ['Ed. Infantil', 'Fund. I', 'Fund. II'],
    obrigatoria: false,
    corVisual: '#92400e',
    aulasSemanaisSugeridas: {},
  },
  {
    nome: 'Redação',
    codigo: 'RED',
    siglaBNCC: null,
    areaDoCOnhecimento: 'Complementar / Parte Diversificada',
    etapasAplicaveis: ['Fund. II', 'Médio'],
    obrigatoria: false,
    corVisual: '#1d4ed8',
    aulasSemanaisSugeridas: {},
  },
  {
    nome: 'Projeto de Vida',
    codigo: 'PV',
    siglaBNCC: null,
    areaDoCOnhecimento: 'Complementar / Parte Diversificada',
    etapasAplicaveis: ['Médio'],
    obrigatoria: false,
    corVisual: '#65a30d',
    aulasSemanaisSugeridas: {},
  },
];

/** Normaliza nome/código para casar seed sem acento (ex.: Portugues → portugues). */
export function normalizeMateriaKey(value?: string | null): string {
  if (!value) return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

const MATERIA_NOME_ALIASES: Record<string, string> = {
  portugues: 'lingua portuguesa',
  'lingua portuguesa': 'lingua portuguesa',
  lp: 'lingua portuguesa',
  por: 'lingua portuguesa',
  matematica: 'matematica',
  mat: 'matematica',
  ciencias: 'ciencias',
  cie: 'ciencias',
  historia: 'historia',
  his: 'historia',
  geografia: 'geografia',
  geo: 'geografia',
  'educacao fisica': 'educacao fisica',
  edf: 'educacao fisica',
  arte: 'arte',
  artes: 'arte',
  art: 'arte',
  ingles: 'lingua inglesa',
  'lingua inglesa': 'lingua inglesa',
  ing: 'lingua inglesa',
  'ensino religioso': 'ensino religioso',
  enr: 'ensino religioso',
  er: 'ensino religioso',
  informatica: 'informatica',
  inf: 'informatica',
  redacao: 'redacao',
  red: 'redacao',
  filosofia: 'filosofia',
  fil: 'filosofia',
  sociologia: 'sociologia',
  soc: 'sociologia',
  fisica: 'fisica',
  fis: 'fisica',
  quimica: 'quimica',
  qui: 'quimica',
  biologia: 'biologia',
  bio: 'biologia',
};

/** Resolve corVisual da matéria (banco) ou fallback do catálogo BNCC / aliases do seed. */
export function resolveMateriaCorVisual(input: {
  corVisual?: string | null;
  nome?: string | null;
  codigo?: string | null;
}): string {
  const stored = input.corVisual?.trim();
  if (stored && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(stored)) return stored;

  const byCodigo = normalizeMateriaKey(input.codigo);
  const byNome = normalizeMateriaKey(input.nome);
  const keys = [byCodigo, byNome].filter(Boolean).map((k) => MATERIA_NOME_ALIASES[k] || k);

  for (const key of keys) {
    const hit = CATALOGO_BNCC.find((item) => {
      const n = normalizeMateriaKey(item.nome);
      const c = normalizeMateriaKey(item.codigo);
      const aliased = MATERIA_NOME_ALIASES[n] || n;
      return n === key || c === key || aliased === key || normalizeMateriaKey(item.siglaBNCC) === key;
    });
    if (hit?.corVisual) return hit.corVisual;
  }

  // Paleta estável por hash do nome (matérias fora do catálogo)
  const palette = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#f97316', '#6366f1'];
  const seed = byNome || byCodigo || 'materia';
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

/** Retorna a etapa de uma série (ex: "6º Ano" → "Fund. II") */
export function getEtapaDaSerie(serie: string): Etapa {
  const lower = serie.toLowerCase();
  if (/maternal|berçário|jardim|pré-escola|infantil|creche/i.test(lower)) return 'Ed. Infantil';
  const ano = parseInt(serie);
  if (!isNaN(ano)) {
    if (ano >= 1 && ano <= 5) return 'Fund. I';
    if (ano >= 6 && ano <= 9) return 'Fund. II';
  }
  if (/1ª|1a|1º/.test(serie) || /série 1|serie 1/i.test(serie)) return 'Médio';
  if (/2ª|2a|2º/.test(serie) || /série 2|serie 2/i.test(serie)) return 'Médio';
  if (/3ª|3a|3º/.test(serie) || /série 3|serie 3/i.test(serie)) return 'Médio';
  return 'Fund. I'; // fallback
}

/** Agrupa o catálogo por área do conhecimento */
export function getCatalogoPorArea(): Record<string, CatalogoItem[]> {
  return CATALOGO_BNCC.reduce((acc, item) => {
    const area = item.areaDoCOnhecimento;
    if (!acc[area]) acc[area] = [];
    acc[area].push(item);
    return acc;
  }, {} as Record<string, CatalogoItem[]>);
}
